import { Box, Flex, Text, Spinner, Select } from '@chakra-ui/react';
import { ButtonText, OutlineCard, Accordion } from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import dateFormat from 'dateformat';
import { useEffect, useState } from 'react';
import { fetchTransferOverviewDetails } from 'src/api/queries';
import Divider from '../Divider';
import { TransferRecord, IRoleType } from '@publishing-engine/domain/models';
import RecommendationBadge from '../RecommendationBadge';
import { SubmissionFilesAccordion } from './SubmissionFilesAccordion';
import { NotesAccordion } from './NotesAccordion';
import { DecisionHistory } from './DecisionHistory';
import { DividerVertical } from './OverviewTab';

interface Props {
  transferHistory: TransferRecord[];
  submissionId: string;
  submissionContextId: string;
  roleContext: IRoleType;
}

const TransferHistoryAccordion = ({
  transferHistory,
  submissionId,
  submissionContextId,
  roleContext
}: Props) => {
  if (!transferHistory || transferHistory.length === 0) {
    return null;
  }

  const items = transferHistory.map(record => ({
    title: `${record.transferedDetail.fromJournalName} → ${record.transferedDetail.toJournalName}`,
    subtitle: `Transferred on ${dateFormat(
      record.transferedDetail.date,
      'mmm d, yyyy'
    )}`,
    content: (
      <TransferHistoryItemContent
        record={record}
        submissionId={submissionId}
        submissionContextId={submissionContextId}
        roleContext={roleContext}
      />
    )
  }));

  return (
    <Accordion
      chakraAccordionProps={{
        allowMultiple: false,
        allowToggle: true
      }}
      accordionItemProps={{
        border: '1px solid',
        borderColor: 'neutralPrimaryBorder',
        marginBottom: 0
      }}
      items={items}
    />
  );
};

const TransferHistoryItemContent = ({
  record,
  submissionId,
  submissionContextId,
  roleContext
}: {
  record: TransferRecord;
  submissionId: string;
  submissionContextId: string;
  roleContext: IRoleType;
}) => {
  const { transferedDetail } = record;
  const [shouldFetch, setShouldFetch] = useState(false);
  const [selectedRound, setSelectedRound] = useState<string>(
    transferedDetail.fromManuscriptId
  );

  const { data: transferDetails, isLoading } = useQuery({
    queryKey: [
      'transfer-overview-details',
      { submissionId, manuscriptId: selectedRound }
    ],
    queryFn: () => fetchTransferOverviewDetails(submissionId, selectedRound),
    enabled: shouldFetch && !!selectedRound,
    retry: 0
  });

  const round = transferDetails?.overviewRevisions?.filter(
    item => item.manuscriptId === selectedRound
  )?.[0];

  const overviewDetailsData = round?.overviewDetails;

  useEffect(() => {
    setShouldFetch(true);
  }, []);

  return (
    <Flex
      px={{ base: 2, sm: 3, md: 4 }}
      py={{ base: 2, sm: 2.5, md: 3 }}
      flexDirection="column"
    >
      {isLoading ? (
        <Flex justifyContent="center" alignItems="center" minH="200px">
          <Spinner />
        </Flex>
      ) : !transferDetails ? (
        <Text textStyle="textContext" color="gray.500" p={4}>
          No transfer details available
        </Text>
      ) : (
        <Flex flexDirection="column" gap={{ base: 3, md: 4 }}>
          {/* Round Selector */}
          {transferDetails?.overviewRevisions &&
            transferDetails.overviewRevisions.length > 0 && (
              <Flex
                align={{ base: 'stretch', md: 'center' }}
                gap={{ base: 1, md: 2 }}
                justifyContent="space-between"
                flexDirection={{ base: 'column', md: 'row' }}
              >
                <Text
                  textStyle="textBody"
                  whiteSpace="nowrap"
                  fontSize={{ base: 'sm', md: 'md' }}
                ></Text>
                <Select
                  width={{ base: '100%', md: '220px' }}
                  value={selectedRound}
                  onChange={e => setSelectedRound(e.target.value)}
                  fontSize={{ base: 'sm', md: 'md' }}
                >
                  {transferDetails.overviewRevisions.map(r => (
                    <option key={r.manuscriptId} value={r.manuscriptId}>
                      {r.isOriginalSubmission
                        ? 'Original Submission'
                        : `Revision - ${r.revisionNumber}`}
                    </option>
                  ))}
                </Select>
              </Flex>
            )}

          {/* Transfer Details */}
          <OutlineCard padding={{ base: 3, md: 4 }}>
            <Flex flexDirection="column" gap={{ base: 2, md: 3 }}>
              <Flex
                justifyContent="space-between"
                flexWrap="wrap"
                gap={{ base: 3, md: 4 }}
                flexDirection={{ base: 'column', sm: 'row' }}
              >
                <Flex
                  flexDirection="column"
                  minW={{ base: 'auto', sm: '200px' }}
                >
                  <Text
                    textStyle="headingLine"
                    fontSize={{ base: 'sm', md: 'md' }}
                  >
                    From Journal
                  </Text>
                  <Text
                    textStyle="textBody"
                    fontSize={{ base: 'sm', md: 'md' }}
                  >
                    {transferedDetail.fromJournalName}
                  </Text>
                  <Text
                    textStyle="textContext"
                    color="gray.500"
                    fontSize={{ base: 'xs', md: 'sm' }}
                  >
                    Manuscript ID: {transferedDetail.fromManuscriptId}
                  </Text>
                </Flex>
                <Flex flexDirection="column">
                  <Text
                    textStyle="headingLine"
                    fontSize={{ base: 'sm', md: 'md' }}
                  >
                    To Journal
                  </Text>
                  <Text
                    textStyle="textBody"
                    fontSize={{ base: 'sm', md: 'md' }}
                  >
                    {transferedDetail.toJournalName}
                  </Text>
                  <Text
                    textStyle="textContext"
                    color="gray.500"
                    fontSize={{ base: 'xs', md: 'sm' }}
                  >
                    Manuscript ID: {transferedDetail.toManuscriptId}
                  </Text>
                </Flex>
                <Flex flexDirection="column">
                  <Text
                    textStyle="headingLine"
                    fontSize={{ base: 'sm', md: 'md' }}
                  >
                    Transfer Date
                  </Text>
                  <Text
                    textStyle="textBody"
                    fontSize={{ base: 'sm', md: 'md' }}
                  >
                    {dateFormat(transferedDetail.date, 'mmm d, yyyy')}
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </OutlineCard>
          <OutlineCard padding={{ base: 2, md: 3 }}>
            <Flex
              gap={{ base: 2, md: 3 }}
              align={{ base: 'flex-start', md: 'center' }}
              flexDirection={{ base: 'column', sm: 'row' }}
              flexWrap="wrap"
            >
              {/* Last Recommendation */}
              <Flex align="center" gap={2}>
                <Text textStyle="textBody" whiteSpace="nowrap">
                  Last recommendation:
                </Text>

                {overviewDetailsData?.cocRecommendationDetails
                  ?.lastRecommendationOrDecision ? (
                  <>
                    <Text textStyle="headingItem" whiteSpace="nowrap">
                      {
                        overviewDetailsData?.cocRecommendationDetails
                          ?.lastRecommendationOrDecisionRole
                      }
                    </Text>
                    <RecommendationBadge
                      recommendation={
                        overviewDetailsData?.cocRecommendationDetails
                          ?.lastRecommendationOrDecision
                      }
                    />
                  </>
                ) : (
                  <Text textStyle="textContext" color="gray.500">
                    No recommendation yet
                  </Text>
                )}
              </Flex>

              <Box display={{ base: 'none', md: 'block' }}>
                <DividerVertical />
              </Box>

              {/* Recommendation Count */}
              <Flex align="center" gap={1}>
                <Text textStyle="textBody" isTruncated>
                  Recommendations:
                </Text>
                {overviewDetailsData?.cocRecommendationDetails
                  ?.recommendationsCount ?? 0}
              </Flex>
            </Flex>

            <Flex
              gap={2}
              align={{ base: 'flex-start', md: 'center' }}
              flexDirection={{ base: 'column', md: 'row' }}
            >
              {/* Notes */}
              <Flex align="center" gap={1}>
                <Text textStyle="textBody">Notes:</Text>
                <ButtonText
                  onClick={() => {}}
                  isDisabled={overviewDetailsData?.notesCount === 0}
                  minW={0}
                >
                  {overviewDetailsData?.notesCount ?? 0}
                </ButtonText>
              </Flex>

              <DividerVertical />
              {/* Target */}
              <Flex align="center" gap={1}>
                <Text textStyle="textBody">Peer Reviews Target:</Text>
                <Text textStyle="textBody">
                  {
                    overviewDetailsData?.peerReviewDetails.peerReviewStats
                      .reviewTarget
                  }
                </Text>
              </Flex>

              <DividerVertical />

              {/* Invited */}
              <Flex align="center" gap={1}>
                <Text textStyle="textBody">Invited:</Text>
                <Text textStyle="textBody">
                  {
                    overviewDetailsData?.peerReviewDetails.peerReviewStats
                      .invited
                  }
                </Text>
              </Flex>

              <DividerVertical />

              {/* Agreed */}
              <Flex align="center" gap={1}>
                <Text textStyle="textBody">Agreed:</Text>
                <Text textStyle="textBody">
                  {overviewDetailsData?.peerReviewDetails.peerReviewStats
                    .agreed ?? 0}
                </Text>
              </Flex>

              <DividerVertical />

              {/* Declined */}
              <Flex align="center" gap={1}>
                <Text textStyle="textBody">Declined:</Text>
                <Text textStyle="textBody">
                  {overviewDetailsData?.peerReviewDetails.peerReviewStats
                    .declined ?? 0}
                </Text>
              </Flex>

              <DividerVertical />

              {/* No Response */}
              <Flex align="center" gap={1}>
                <Text textStyle="textBody">No response:</Text>
                <Text textStyle="textBody">
                  {overviewDetailsData?.peerReviewDetails.peerReviewStats
                    .noResponse ?? 0}
                </Text>
              </Flex>

              <DividerVertical />

              {/* Completed */}
              <Flex align="center" gap={1}>
                <Text textStyle="textBody">Completed:</Text>
                <Text textStyle="textBody">
                  {overviewDetailsData?.peerReviewDetails.peerReviewStats
                    .completed ?? 0}
                </Text>
              </Flex>
            </Flex>
          </OutlineCard>

          {/* Overview Details from Transfer */}
          <Flex
            justifyContent="space-between"
            gap={{ base: 2, md: 4 }}
            flexDirection={{ base: 'column', md: 'row' }}
          >
            <Box width={{ base: '100%', md: '50%' }}>
              {/* Recommendations & Decisions */}
              <OutlineCard padding={{ base: 3, md: 4 }}>
                <Text
                  textStyle="headingLine"
                  fontSize={{ base: 'sm', md: 'md' }}
                  mb={3}
                >
                  Recommendations (
                  {overviewDetailsData?.cocRecommendationDetails
                    ?.recommendationsCount ?? 0}
                  )
                </Text>
                {Divider}
                {!overviewDetailsData?.cocRecommendationDetails?.cycles ||
                overviewDetailsData.cocRecommendationDetails.cycles.length ===
                  0 ? (
                  <Flex
                    justifyContent="flex-start"
                    alignItems="center"
                    padding={4}
                    minH="100px"
                  >
                    <Text textStyle="textContext">
                      No recommendations have been made.
                    </Text>
                  </Flex>
                ) : (
                  <Flex
                    gap={2}
                    flexDirection="column"
                    padding={2}
                    maxH="250px"
                    overflowY="auto"
                  >
                    {overviewDetailsData.cocRecommendationDetails.cycles.map(
                      (cycle, cycleIdx) => (
                        <OutlineCard key={cycleIdx}>
                          {cycle.participants.map((participant, idx) => (
                            <Flex
                              key={idx}
                              padding={2}
                              gap={1}
                              flexDirection="column"
                            >
                              <Text textStyle="textBody">
                                {participant.role}: {participant.userFirstName}{' '}
                                {participant.userLastName}
                              </Text>
                              {participant.recommendation ? (
                                <Box display="flex" gap={2} alignItems="center">
                                  <RecommendationBadge
                                    recommendation={
                                      participant.recommendation.decision ?? ''
                                    }
                                  />
                                  <Text textStyle="textContext">
                                    Submitted on{' '}
                                    {dateFormat(
                                      participant.completedAt,
                                      'mmm d, yyyy'
                                    )}
                                  </Text>
                                </Box>
                              ) : (
                                <Text textStyle="textContext" color="gray.500">
                                  {participant.status === 'awaiting'
                                    ? 'Awaiting'
                                    : 'Interrupted'}
                                </Text>
                              )}
                              {cycle.participants.length - 1 !== idx && (
                                <Box py={1}>{Divider}</Box>
                              )}
                            </Flex>
                          ))}
                        </OutlineCard>
                      )
                    )}
                  </Flex>
                )}
              </OutlineCard>
            </Box>
            <Flex width={{ base: '100%', md: '50%' }}>
              {/* Peer Reviews */}
              <OutlineCard padding={{ base: 3, md: 4 }} width="100%">
                <Text
                  textStyle="headingLine"
                  fontSize={{ base: 'sm', md: 'md' }}
                  mb={3}
                >
                  Peer Reviews (
                  {overviewDetailsData?.peerReviewDetails?.participants
                    ?.length ?? 0}
                  )
                </Text>
                {Divider}
                {!overviewDetailsData?.peerReviewDetails?.participants ||
                overviewDetailsData.peerReviewDetails.participants.length ===
                  0 ? (
                  <Flex
                    justifyContent="flex-start"
                    alignItems="center"
                    padding={4}
                    minH="100px"
                  >
                    <Text textStyle="textContext">
                      No peer reviews have been made.
                    </Text>
                  </Flex>
                ) : (
                  <Flex
                    gap={2}
                    flexDirection="column"
                    padding={2}
                    maxH="250px"
                    overflowY="auto"
                  >
                    {overviewDetailsData.peerReviewDetails.participants.map(
                      (participant, idx) => (
                        <OutlineCard key={idx} padding={2}>
                          <Flex gap={1} flexDirection="column">
                            <Text textStyle="textBody">
                              {participant.reviewer?.firstName}{' '}
                              {participant.reviewer?.lastName}
                            </Text>
                            {participant.reviewerRecommendationData ? (
                              <Box display="flex" gap={2} alignItems="center">
                                <RecommendationBadge
                                  recommendation={
                                    participant.reviewerRecommendationData
                                      ?.recommendation ?? ''
                                  }
                                />
                                <Text textStyle="textContext">
                                  Submitted on{' '}
                                  {dateFormat(
                                    participant.reviewerRecommendationData
                                      ?.date,
                                    'mmm d, yyyy'
                                  )}
                                </Text>
                              </Box>
                            ) : (
                              <Text textStyle="textContext" color="gray.500">
                                Awaiting Review
                              </Text>
                            )}
                          </Flex>
                        </OutlineCard>
                      )
                    )}
                  </Flex>
                )}
              </OutlineCard>
            </Flex>
          </Flex>

          <Flex flexDirection="column" gap={{ base: 3, md: 4 }}>
            {/* Decision History */}
            {overviewDetailsData?.decisionHistory && (
              <DecisionHistory
                decisionHistoryCount={
                  overviewDetailsData?.decisionHistoryCount ?? 0
                }
                submissionContextId={submissionContextId}
                decisionHistory={overviewDetailsData.decisionHistory}
                roleContext={roleContext}
                onClick={() => {}}
              />
            )}

            {/* Submission Files */}
            {overviewDetailsData?.submissionFileDetails?.fileSet && (
              <SubmissionFilesAccordion
                fileset={overviewDetailsData.submissionFileDetails.fileSet}
                totalFileCount={
                  overviewDetailsData.submissionFileDetails.totalFileCount ?? 0
                }
                submissionContextId={submissionContextId}
              />
            )}

            {/* Notes */}
            <NotesAccordion
              totalNotesCount={overviewDetailsData?.notesCount ?? 0}
              notes={overviewDetailsData?.notes}
            />
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

export default TransferHistoryAccordion;
